/**
 * @file CardLookGroup.tsx
 * @description Группа «Вид карточки» во вкладке «Каталог → Карточка товара»
 * (SB31, дополнение 2026-10-11; SB122 — группа-карточка со сводкой «как у
 * всех» / «своё»): «Как у всех карточек сайта» — скругление, рамка и тень
 * берутся из «Оформление → Кнопки и карточки» (ссылка туда), «Свои» — те
 * же поля, что у темы (`CardLookFields`), с образцом карточки товара. При
 * переключении на «Свои» подставляются текущие значения темы — карточка не
 * меняется, пока их не тронут.
 *
 * Решение пользователя 2026-10-12: при «Как у всех» под переключателем —
 * текущие значения оформления («Сейчас: 8/6 px · рамка 1 px · тень 0 % —
 * задаётся в «Оформление → Кнопки и карточки» →», ссылка с `group=cards`).
 *
 * SB120 этап 2 (uiux-sb120-stage2 §2, §5; db-design-sb120-stage2 §1 каталог
 * `card.look own {radius, cards}`): числа вместо ступеней; у карточки товара
 * только скругление карточки (поля и бейджи — общие темы).
 */

import {
  CATALOG_CARD_LOOK_LABEL,
  CATALOG_CARD_LOOK_MODES,
  CATALOG_CARD_LOOK_MODE_OPTIONS,
  DEFAULT_SITE_THEME,
  type CatalogCardLook,
  type SiteColors,
  type SiteTheme,
} from "@chaiberi/shared";
import { Link } from "react-router";

import { FieldHint, SettingsGroup } from "../../ui/settings-groups";
import { CardLookFields } from "../appearance/CardLookFields";
import { CardSample } from "../appearance/ShapeSamples";
import { Segmented } from "../Segmented";
import { useBuilderLink } from "../../../hooks/site-builder/useBuilderLink";
import { cardLookText } from "../../../lib/site-view/card-shapes";

type Shapes = SiteTheme["shapes"];

const MODES = CATALOG_CARD_LOOK_MODES.map((value) => ({ value, label: CATALOG_CARD_LOOK_MODE_OPTIONS[value] }));

interface Props {
  look: CatalogCardLook;
  onChange: (look: CatalogCardLook) => void;
  /** Скругление, рамка и тень карточек темы — для «Как у всех» и первого «Свои». */
  themeShapes: Pick<Shapes, "radius" | "cards">;
  colors: SiteColors | null;
}

export function CardLookGroup({ look, onChange, themeShapes, colors }: Props) {
  const link = useBuilderLink();
  const own = look.mode === "own" ? look : null;
  // Цвета темы ещё не пришли — поля не прячем, образец и предупреждения по цветам по умолчанию.
  const palette = colors ?? DEFAULT_SITE_THEME.colors;
  const siteText = cardLookText({ radius: themeShapes.radius.card, field: themeShapes.radius.field, cards: themeShapes.cards });

  return (
    <SettingsGroup
      id="look"
      title={CATALOG_CARD_LOOK_LABEL}
      tocLabel="Вид"
      hint="Скругление, рамка и тень карточки товара."
      summary={own ? `своё · ${cardLookText(own)}` : `как у всех · ${siteText}`}
    >
      <Segmented
        aria-label={CATALOG_CARD_LOOK_LABEL}
        options={MODES}
        value={look.mode}
        onChange={(mode) => onChange(mode === "own" ? { mode, radius: themeShapes.radius.card, cards: themeShapes.cards } : { mode })}
      />
      {own ? (
        <CardLookFields
          value={{ radius: own.radius, cards: own.cards }}
          onChange={(value) => onChange({ mode: "own", radius: value.radius, cards: value.cards })}
          colors={palette}
          of="карточки товара"
          radiusLabel="Скругление карточки"
          sample={<CardSample look={own} colors={palette} label="Образец карточки товара" title="Товар" caption="от 350 ₽" />}
        />
      ) : (
        <FieldHint className="mt-0">
          Сейчас: {siteText} — задаётся в{" "}
          <Link to={link({ panel: "appearance", sub: "shapes", group: "cards" })} className="inline-flex min-h-12 items-center font-medium text-primary hover:underline">
            «Оформление → Кнопки и карточки» →
          </Link>
        </FieldHint>
      )}
    </SettingsGroup>
  );
}
